import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle, wiederholbare In-Dialog-Hilfe fuer den Zeiterfassungs-Detail-
 * Dialog (`ShowTimeRecordingDetailsComponent`). Wird ueber den Hilfe-Button
 * (`<app-dialog-help-button>`) im Dialog-Header manuell gestartet.
 *
 * Bewusst getrennt von der grossen Funnel-Tour `TIME_RECORDING_ONBOARDING_TOUR`
 * (category 'setup', Step-IDs `tr-*`), die denselben Dialog EINMALIG als
 * gefuehrten Hands-on-Walkthrough durchlaeuft. Diese Tour ist erklaerungs-
 * basiert (manuelles Weiterklicken), wiederholbar und nutzt dieselben Anchors
 * — eine Kollision ist ausgeschlossen, da nie beide gleichzeitig laufen.
 *
 * Erklaert in 7 Steps: Dialog-Ueberblick → Summen → Compliance-Hinweise →
 * Zeiteintraege → Hinzufuegen-Button → Neuer-Eintrag-Formular (Card wird per
 * synthetischem Klick programmatisch geoeffnet und danach wieder geschlossen,
 * Pattern analog `openAdvancedAccordion` in `auto-suggest-tutorial.config.ts`)
 * → Fusszeile (Modus + Tastatur-Kuerzel).
 *
 * Datenabhaengige Steps (Summen, Compliance, Eintraege) tragen `skipIf` auf die
 * tatsaechliche Anchor-Existenz im DOM — an einem leeren Tag fallen sie sauber
 * weg, ohne leeren Spotlight.
 */

/**
 * Oeffnet das "Neuer Eintrag"-Formular ueber einen synthetischen Klick auf den
 * "Zeiteintrag hinzufuegen"-Button. Idempotent: oeffnet nur, wenn die Edit-Card
 * noch nicht im DOM ist und der Add-Button existiert (canAdd).
 */
function openNewEntryForm(): void {
  const add = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="${AnchorId.TimeEntryAddButton}"]`
  );
  const card = document.querySelector(
    `[onboardingAnchor="${AnchorId.TimeEntryNewCard}"]`
  );
  if (add && !card) {
    add.click();
  }
}

/** Schliesst die Edit-Card wieder (Cleanup nach Step-Verlassen) via Cancel-Button. */
function closeNewEntryForm(): void {
  const cancel = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="${AnchorId.TimeEntryCancelButton}"]`
  );
  if (cancel) {
    cancel.click();
  }
}

const hasAnchor = (anchor: AnchorId): boolean =>
  typeof document !== 'undefined' &&
  !!document.querySelector(`[onboardingAnchor="${anchor}"]`);

export const SHOW_TIME_RECORDING_DETAILS_HELP_TOUR: TourConfig = {
  id: TourFeature.TimeRecordingDetailsHelp,
  version: 1,
  title: 'TIME_RECORDING_DETAILS_HELP_TOUR_TITLE',
  description: 'TIME_RECORDING_DETAILS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Zeiterfassung navigieren. Der Details-Dialog hat
  // KEINEN deterministischen Opener-Button — er oeffnet via Klick auf eine
  // Tageszeile im Kalender. Phase-1-Loesung: `route` only. User landet
  // auf der richtigen Seite und oeffnet selbst.
  launchContext: {
    route: '/time-recording',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 80,
  // Bewusst 'feature-discovery' (nicht 'contextual-help'): wir wollen die
  // klassische Guided Tour mit Spotlight + Voice-Over, nicht die (i)-Icon-
  // HelpMode-Variante. Begruendung ausfuehrlich in auto-suggest-tutorial.config.ts.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TIME_RECORDING_DETAILS_HELP_TOUR_TITLE',
      steps: [
        {
          // Welcome erklaert den ganzen Dialog — Spotlight am Wrapper.
          id: 'welcome',
          anchor: AnchorId.TimeRecordingDialog,
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.WELCOME.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'summary',
          anchor: AnchorId.TimeRecordingWorkSummary,
          additionalSpotlightAnchors: [AnchorId.TimeRecordingBreakSummary],
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.SUMMARY.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.SUMMARY.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => !hasAnchor(AnchorId.TimeRecordingWorkSummary),
        },
        {
          id: 'compliance',
          anchor: AnchorId.TimeRecordingCompliance,
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.COMPLIANCE.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.COMPLIANCE.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => !hasAnchor(AnchorId.TimeRecordingCompliance),
        },
        {
          id: 'entries',
          anchor: AnchorId.TimeEntryCard,
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.ENTRIES.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.ENTRIES.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => !hasAnchor(AnchorId.TimeEntryCard),
        },
        {
          id: 'add',
          anchor: AnchorId.TimeEntryAddButton,
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.ADD.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.ADD.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => !hasAnchor(AnchorId.TimeEntryAddButton),
        },
        {
          // Oeffnet die Edit-Card programmatisch, erklaert Typ-Auswahl, Zeiten
          // und Speichern/Verwerfen in einem Step, schliesst sie danach wieder.
          id: 'new-entry',
          anchor: AnchorId.TimeEntryNewCard,
          additionalSpotlightAnchors: [
            AnchorId.TimeEntryTypeSelect,
            AnchorId.TimeEntrySaveButton,
          ],
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.NEW_ENTRY.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.NEW_ENTRY.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: openNewEntryForm,
          onAfterHide: closeNewEntryForm,
          // Step nur moeglich, wenn die Card geoeffnet werden kann (Add-Button
          // vorhanden) oder bereits offen ist.
          skipIf: () =>
            !hasAnchor(AnchorId.TimeEntryAddButton) &&
            !hasAnchor(AnchorId.TimeEntryNewCard),
        },
        {
          id: 'footer',
          anchor: AnchorId.TimeRecordingFooterHint,
          title: 'TUTORIAL.TIME_RECORDING_DETAILS.FOOTER.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_DETAILS.FOOTER.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Schliesst eine evtl. noch offene Edit-Card aus Step 6 deterministisch:
          // `onAfterHide` des new-entry-Steps feuert auf Vorwaerts-Transitionen
          // nicht zuverlaessig, daher hier beim Betreten erzwingen. So zeigt die
          // Fusszeile den Stand-by-/Editierbar-Hinweis (passend zum Text) und der
          // User schliesst die Tour nicht mit einem verwaisten Add-Formular ab.
          onBeforeShow: closeNewEntryForm,
          // Letzter Step: kein Zurueck, nur "Abschliessen".
          showPrevButton: false,
        },
      ],
    },
  ],
};
